// Format — reference-only registry, not a BC.
//
// Per `.docs/v1/design/bounded-contexts.md` §13 and `surface.md` §7.
// No state, no commands, no events. Adding a Format is a *code*
// change: a new `const` ref here and a shell-side adapter. Dynamic
// Format registration is post-MVP with a real user need.
//
// Composition: `Format.Id` and `Format.Ref` are the branded types.
// The five built-in refs (`pdf` / `html` / `markdown` / `text` /
// `torus`) are domain *values* and live at `Format.*` top-level per
// the rule (values, not types, do not fan out into submodules of
// their own). The `registry` map folds them into one lookup.
export * as Id from "./format/id.js";
export * as Ref from "./format/ref.js";
export { canvas, catalogue, containerLevels, html, markdown, pdf, registry, text, torus, };
import * as IdMod from "./format/id.js";
import * as RefMod from "./format/ref.js";
import * as Version from "./versioning/version.js";
// === MVP registry ===
//
// Five sentinel refs at `v1`. Design-side these are referred to as
// `F_pdf` / `F_html` / `F_markdown` / `F_text` / `F_torus`; ids
// preserve the prefix so bundle serialisations line up with the
// design docs.
//
// `F_torus` is the OLI-Torus course-digest .zip — an LMS-ingestible
// archive. Its adapter lives in `@bilda/infra/torus`; a T→F packaging
// (`P_freeform_torus`) targets it from the freeform Template.
//
// `F_canvas` is the Instructure-Canvas Common Cartridge `.imscc`
// archive (CC 1.1 manifest + QTI 1.2 Classic-Quiz assessments). Its
// adapter lives in `@bilda/infra/canvas`; the T→F packaging
// (`P_pqbl_torus_canvas`) targets it from the pqbl-torus Template.
const pdf = RefMod.mk(IdMod.mk("F_pdf"), Version.v1);
const html = RefMod.mk(IdMod.mk("F_html"), Version.v1);
const markdown = RefMod.mk(IdMod.mk("F_markdown"), Version.v1);
const text = RefMod.mk(IdMod.mk("F_text"), Version.v1);
const torus = RefMod.mk(IdMod.mk("F_torus"), Version.v1);
const canvas = RefMod.mk(IdMod.mk("F_canvas"), Version.v1);
const registry = new Map([
    [pdf.id, pdf],
    [html.id, html],
    [markdown.id, markdown],
    [text.id, text],
    [torus.id, torus],
    [canvas.id, canvas],
]);
// === Container levels ===
//
// How many levels of containers below the course a format nests. A Common
// Cartridge for Canvas has one (modules; a deeper container becomes a
// heading inside its module). Torus nests freely (units, modules,
// sections), and so do the document formats (headings), the default.
const containerLevels = (format) => format.id === canvas.id ? 1 : Number.POSITIVE_INFINITY;
const catalogue = [
    {
        id: "F_torus",
        description: "OLI-Torus course-digest .zip — an LMS-ingestible archive.",
        guidance: "Reach for when the Author wants to import material into an LMS (Torus). Pairs with template **T_pqbl_torus** for real MCQ activities; pairing with **T_freeform** yields a Torus digest wrapping the raw text as one paragraph (fine for prose, not for quizzes). Chain: create_material_from_source(templateRef='T_pqbl_torus', ...) → create_package_from_material(targetFormat='F_torus') → export_package writes the .zip to disk.",
    },
    {
        id: "F_text",
        description: "Plain UTF-8 text — passthrough of the material's content.",
        guidance: "Reach for when the Author wants raw text (copy-paste, plain export, or an intermediate step). Works with any material. Chain: create_package_from_material(fromFormatRef=F_text) → export_package writes a .txt file.",
    },
    {
        id: "F_html",
        description: "Self-contained HTML document — a single portable .html file with all styling inlined, opens standalone in any browser.",
        guidance: "The keystone physical/portable output — reach for when the Author wants a handout, a shareable web page, or a printable file rather than an LMS import. Works with ANY material: a pqbl-torus quiz renders as a structured handout with correct answers marked and feedback shown (doubles as an answer key); freeform content wraps as a readable document. Carries an @media print stylesheet, so the same file prints or Save-as-PDFs cleanly. Chain: create_package_from_material(targetFormat='F_html') → export_package writes the .html file.",
    },
    {
        id: "F_pdf",
        description: "Print-ready PDF — a paginated document a teacher can print directly (worksheet / handout).",
        guidance: "Reach for when the Author wants a printable file rather than a web page or an LMS import. Works with ANY material: a pqbl-torus quiz becomes a paginated handout with correct answers marked; freeform content flows as a text document. (Current output is a text-layout PDF — full visual fidelity via HTML→print is a later upgrade.) Chain: create_package_from_material(targetFormat='F_pdf') → export_package writes the .pdf file.",
    },
    {
        id: "F_canvas",
        description: "Instructure-Canvas Common Cartridge .imscc — an LMS-ingestible quiz archive.",
        guidance: "Reach for when the Author wants to import quiz material into Canvas (Instructure). Pairs with template **T_pqbl_torus**: each Page becomes a Canvas Classic Quiz (QTI 1.2 assessment) and each MCQ a multiple_choice_question. Learning objectives and skills have no Canvas home and are dropped (declared fidelity loss `lost_objective_alignment`). Chain: create_material_from_source(templateRef='T_pqbl_torus', ...) → create_package_from_material(targetFormat='F_canvas') → export_package writes the .imscc archive to disk.",
    },
];
//# sourceMappingURL=format.js.map